import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calculator, TrendingUp, DollarSign, Percent, Calendar, BarChart3, Crown, ChevronRight, ArrowLeft } from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvestorModePage = () => {
  const navigate = useNavigate();
  const [calculating, setCalculating] = useState(false);
  const [results, setResults] = useState(null);
  
  const [formData, setFormData] = useState({
    property_id: 'sample-property',
    purchase_price: 10000000,
    down_payment_percent: 25,
    loan_years: 25,
    interest_rate: 4.5,
    monthly_rent: 50000,
    annual_appreciation: 5.0
  });

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/investor/calculate-roi`, formData);
      setResults(response.data);
      toast.success('Investment analysis complete!');
    } catch (error) {
      toast.error('Failed to calculate. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      {/* Navigation */}
      <Nav showBack activePage="/investor" />

      <div className="max-w-[1440px] mx-auto px-8 py-16">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 className="font-playfair text-5xl font-semibold mb-4">Investor Mode</h1>
          <p className="text-text-secondary text-lg mb-6">Calculate ROI, rental yields, and investment projections for Dubai properties</p>
          
          {/* Portfolio Insights Banner */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-gold/20 to-purple-500/20 border border-gold/30 rounded-xl p-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-gold" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-lg">Portfolio Insights Dashboard</h3>
                  <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Premium
                  </span>
                </div>
                <p className="text-text-secondary text-sm">Track asset growth, diversification, lifestyle score & ROI projections</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/investor/insights')}
              className="bg-gold text-black hover:bg-gold-light"
            >
              Open Dashboard
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="w-8 h-8 text-gold" />
              <h2 className="font-playfair text-3xl font-semibold">ROI Calculator</h2>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-text-primary mb-2 block">Purchase Price (AED)</Label>
                <Input
                  type="number"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) })}
                  className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Down Payment (%)</Label>
                <Input
                  type="number"
                  value={formData.down_payment_percent}
                  onChange={(e) => setFormData({ ...formData, down_payment_percent: parseFloat(e.target.value) })}
                  className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Loan Period (Years)</Label>
                <Input
                  type="number"
                  value={formData.loan_years}
                  onChange={(e) => setFormData({ ...formData, loan_years: parseInt(e.target.value) })}
                  className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Interest Rate (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.interest_rate}
                  onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) })}
                  className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Expected Monthly Rent (AED)</Label>
                <Input
                  type="number"
                  value={formData.monthly_rent}
                  onChange={(e) => setFormData({ ...formData, monthly_rent: parseFloat(e.target.value) })}
                  className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4"
                />
              </div>

              <div>
                <Label className="text-text-primary mb-2 block">Annual Appreciation (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.annual_appreciation}
                  onChange={(e) => setFormData({ ...formData, annual_appreciation: parseFloat(e.target.value) })}
                  className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none px-0 py-4"
                />
              </div>

              <Button
                onClick={handleCalculate}
                disabled={calculating}
                className="w-full bg-gold text-black hover:bg-gold-light transition-all duration-500 uppercase tracking-widest text-sm font-bold py-6 rounded-sm"
              >
                {calculating ? 'Calculating...' : 'Calculate Investment'}
              </Button>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            {results ? (
              <>
                {/* Investment Summary */}
                <div className="glass-card p-8">
                  <h3 className="font-playfair text-2xl font-semibold mb-6">Investment Summary</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                      <span className="text-text-secondary">Down Payment</span>
                      <span className="text-gold font-bold text-lg">{formatCurrency(results.down_payment)}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                      <span className="text-text-secondary">Loan Amount</span>
                      <span className="text-text-primary font-semibold">{formatCurrency(results.loan_amount)}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                      <span className="text-text-secondary">Monthly Mortgage</span>
                      <span className="text-text-primary font-semibold">{formatCurrency(results.monthly_mortgage)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Monthly Cash Flow</span>
                      <span className={`font-bold text-lg ${results.monthly_cash_flow > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(results.monthly_cash_flow)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Returns Analysis */}
                <div className="glass-card p-8">
                  <h3 className="font-playfair text-2xl font-semibold mb-6">Returns Analysis (5 Years)</h3>
                  <div className="space-y-6">
                    <div className="bg-gold/10 border border-gold/30 p-6 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <TrendingUp className="w-6 h-6 text-gold" />
                        <span className="text-text-secondary">ROI (5 Years)</span>
                      </div>
                      <div className="text-4xl font-bold text-gold">{results.roi_5y_percent}%</div>
                    </div>

                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                      <span className="text-text-secondary">Rental Yield</span>
                      <span className="text-gold font-bold">{results.rental_yield_percent}%</span>
                    </div>

                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                      <span className="text-text-secondary">Capital Appreciation</span>
                      <span className="text-emerald-400 font-bold">{formatCurrency(results.capital_appreciation_5y)}</span>
                    </div>

                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                      <span className="text-text-secondary">Future Value (5Y)</span>
                      <span className="text-text-primary font-semibold">{formatCurrency(results.future_value_5y)}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Total Return</span>
                      <span className="text-gold font-bold text-lg">{formatCurrency(results.total_return_5y)}</span>
                    </div>

                    {results.break_even_months > 0 && (
                      <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-emerald-400" />
                          <span className="text-text-secondary">Break-even in <span className="text-emerald-400 font-bold">{results.break_even_months} months</span></span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="glass-card p-12 text-center">
                <DollarSign className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
                <p className="text-text-secondary text-lg">Enter investment details to see projections</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default InvestorModePage;
